import React, { useState } from 'react';
import { Check, ChevronRight, Circle, ListChecks, RotateCcw, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../../contexts/OnboardingContext';

const OnboardingWidget = () => {
  const onboarding = useOnboarding();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const state = onboarding?.state;

  if (!state?.enabled || state.total_count === 0) return null;
  const journeyCompleted = state.status === 'completed';

  const goToStep = async (step) => {
    await onboarding.interaction('started', step.id);
    setOpen(false);
    navigate(step.route);
  };

  const showStep = async (step) => {
    await onboarding.interaction('tour_started', step.id);
    setOpen(false);
    navigate(step.route);
    window.setTimeout(() => window.dispatchEvent(new CustomEvent('odontoassist:start-guided-tour', { detail: { stepId: step.id } })), 250);
  };

  const toggleTips = async () => {
    setSavingPreferences(true);
    try { await onboarding.updatePreferences({ proactive_tips: state.preferences?.proactive_tips === false }); }
    finally { setSavingPreferences(false); }
  };

  const restoreTips = async () => {
    setSavingPreferences(true);
    try { await onboarding.resetTips(); }
    finally { setSavingPreferences(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setOpen(true);
          onboarding.interaction('opened');
          onboarding.refresh();
        }}
        className="fixed bottom-24 right-6 z-40 flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        aria-label={journeyCompleted ? 'Abrir guia rápido' : 'Abrir meus primeiros passos'}
      >
        <ListChecks className="h-5 w-5" />
        <span className="hidden sm:inline">{journeyCompleted ? 'Guia rápido' : 'Primeiros passos'}</span>
        {!journeyCompleted && <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs">{state.completed_count}/{state.total_count}</span>}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onMouseDown={(event) => event.target === event.currentTarget && setOpen(false)}>
          <section className="h-full w-full max-w-md overflow-y-auto bg-white shadow-2xl" role="dialog" aria-modal="true" aria-labelledby="onboarding-title">
            <div className="sticky top-0 flex items-start justify-between border-b bg-white p-5">
              <div>
                <h2 id="onboarding-title" className="text-xl font-bold text-gray-900">{journeyCompleted ? 'Guia rápido' : 'Seus primeiros passos'}</h2>
                <p className="mt-1 text-sm text-gray-600">{journeyCompleted ? 'Reveja quando quiser como usar os principais recursos.' : 'Aprenda usando o próprio OdontoAssist AI.'}</p>
              </div>
              <button type="button" onClick={() => { setOpen(false); onboarding.interaction('closed'); }} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100" aria-label="Fechar"><X className="h-5 w-5" /></button>
            </div>

            <div className="p-5">
              {!journeyCompleted && <div className="mb-6">
                <div className="mb-2 flex justify-between text-sm"><span className="font-medium text-gray-700">Progresso essencial</span><span className="text-gray-500">{state.completed_count} de {state.total_count}</span></div>
                <div className="h-2 overflow-hidden rounded-full bg-gray-200"><div className="h-full rounded-full bg-indigo-600 transition-all" style={{ width: `${(state.completed_count / state.total_count) * 100}%` }} /></div>
              </div>}

              {state.experience_profile === 'manual' && (
                <div className="mb-5 rounded-xl border border-blue-200 bg-blue-50 p-4">
                  <p className="text-sm font-semibold text-blue-900">Escolha seus módulos quando quiser</p>
                  <p className="mt-1 text-sm text-blue-800">
                    O menu começou no modo essencial. Essa configuração é opcional e pode ser alterada depois.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setOpen(false); navigate('/configuracoes?tab=menu'); }}
                    className="mt-3 text-sm font-semibold text-blue-700 hover:text-blue-900"
                  >
                    Configurar módulos do menu
                  </button>
                </div>
              )}

              <div className="space-y-3">
                {state.steps.map((step) => {
                  const done = step.status === 'completed';
                  return (
                    <div key={step.id} className={`rounded-xl border p-4 ${done ? 'border-green-200 bg-green-50' : state.next_step?.id === step.id ? 'border-indigo-300 bg-indigo-50' : 'border-gray-200'}`}>
                      <div className="flex gap-3">
                        {done ? <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-600 text-white"><Check className="h-4 w-4" /></span> : <Circle className="mt-0.5 h-6 w-6 shrink-0 text-gray-400" />}
                        <div className="min-w-0 flex-1">
                          <h3 className={`font-semibold ${done ? 'text-green-900' : 'text-gray-900'}`}>{step.title}</h3>
                          <p className="mt-1 text-sm text-gray-600">{step.description}</p>
                          {!done && step.requirements?.length > 0 && (
                            <ul className="mt-3 space-y-1.5 text-xs text-gray-700">
                              {step.requirements.map((requirement) => (
                                <li key={requirement.id} className="flex items-start gap-2">
                                  {requirement.completed
                                    ? <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-green-600" />
                                    : <Circle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />}
                                  <span className={requirement.completed ? 'text-green-700' : ''}>{requirement.title}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                          {!done && (
                            <div className="mt-3 flex flex-wrap gap-3"><button type="button" onClick={() => goToStep(step)} className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-700 hover:text-indigo-900">{step.action_label}<ChevronRight className="h-4 w-4" /></button><button type="button" onClick={() => showStep(step)} className="text-sm font-semibold text-gray-600 hover:text-gray-900">Me mostrar</button></div>
                          )}
                          {done && (
                            <button
                              type="button"
                              onClick={() => showStep(step)}
                              className="mt-3 text-sm font-semibold text-green-700 hover:text-green-900"
                            >
                              Rever como fazer
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {journeyCompleted && <div className="mt-6 rounded-xl bg-green-50 p-4 text-sm text-green-900"><strong>Configuração inicial concluída.</strong><br />Este guia permanece disponível apenas para você rever os principais recursos quando precisar.</div>}

              {!journeyCompleted && <div className="mt-6 border-t pt-5">
                <h3 className="text-sm font-semibold text-gray-900">Preferências de orientação</h3>
                <div className="mt-3 flex items-center justify-between gap-4 rounded-lg bg-gray-50 p-3">
                  <div><p className="text-sm font-medium text-gray-800">Dicas contextuais</p><p className="text-xs text-gray-500">Cada etapa aparece uma única vez por sessão, somente na tela relacionada.</p></div>
                  <button type="button" onClick={toggleTips} disabled={savingPreferences} aria-pressed={state.preferences?.proactive_tips !== false} className={`relative h-6 w-11 shrink-0 rounded-full transition ${state.preferences?.proactive_tips !== false ? 'bg-indigo-600' : 'bg-gray-300'} disabled:opacity-50`}><span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${state.preferences?.proactive_tips !== false ? 'left-5' : 'left-0.5'}`} /></button>
                </div>
                <button type="button" onClick={restoreTips} disabled={savingPreferences} className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-900 disabled:opacity-50"><RotateCcw className="h-4 w-4" />Restaurar dicas dispensadas</button>
              </div>}
            </div>
          </section>
        </div>
      )}
    </>
  );
};

export default OnboardingWidget;
